/**
 * API service for Agent Studio feature.
 */

import { AGENT_STUDIO_CHAT_EVENT_TYPES } from '@/types/promptExplorer'
import type {
  PromptCatalog,
  CombinedPromptResponse,
  PromptPreviewResponse,
  CustomAgent,
  CustomAgentVersion,
  TraceContext,
  ChatMessage,
  ChatContext,
  OpusChatEvent,
  SuggestionSubmission,
  SuggestionResponse,
  ToolInfo,
  ModelOption,
  ToolLibraryItem,
  AgentTemplate,
  GroupOption,
  ToolIdeaRequest,
  OwnedToolIdeaRequest,
  ToolIdeaConversationEntry,
  WorkshopAction,
  WorkshopActionRequest,
} from '@/types/promptExplorer'
import { readCurationApiError } from '@/features/curation/services/api'
import { logger } from '@/services/logger'
import {
  AGENT_STUDIO_CHAT_HISTORY_KIND,
  fetchChatHistoryDetail,
  fetchChatHistoryList,
  type ChatHistoryDetailRequest,
  type ChatHistoryDetailResponse,
  type ChatHistoryListRequest,
  type ChatHistoryListResponse,
} from '@/services/chatHistoryApi'
import { normalizeChatHistoryValue } from '@/lib/chatHistoryNormalization'
import type { AgentOutputContract, AgentExecutionReceipt, AgentExecutionRevision, AgentExecutionRevisionPage, GenericProfilePin } from '@/types/agentExecution'
import type { GenericProfileContract } from '@/services/genericProfileService'

const BASE_URL = '/api/agent-studio'

const AGENT_STUDIO_CHAT_EVENT_TYPE_SET = new Set<string>(AGENT_STUDIO_CHAT_EVENT_TYPES)

type AgentStudioStreamProtocolReason =
  | 'malformed_json'
  | 'invalid_shape'
  | 'unknown_type'
  | 'correlation_mismatch'
  | 'missing_terminal'

export class AgentStudioStreamProtocolError extends Error {
  readonly reason: AgentStudioStreamProtocolReason
  readonly eventType?: OpusChatEvent['type']

  constructor(reason: AgentStudioStreamProtocolReason, eventType?: OpusChatEvent['type']) {
    super('AI Chat received an unexpected stream response. Please retry.')
    this.name = 'AgentStudioStreamProtocolError'
    this.reason = reason
    this.eventType = eventType
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function hasString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0
}

function hasOptionalString(value: unknown): value is string | null | undefined {
  return value === undefined || value === null || typeof value === 'string'
}

/**
 * Validate the public Agent Studio SSE shape before UI state sees it. Errors
 * deliberately contain no raw event payload, provider response, or tool data.
 */
export function parseAgentStudioChatEvent(value: unknown): OpusChatEvent {
  if (!isRecord(value) || !hasString(value.type)) {
    throw new AgentStudioStreamProtocolError('invalid_shape')
  }
  if (!AGENT_STUDIO_CHAT_EVENT_TYPE_SET.has(value.type)) {
    throw new AgentStudioStreamProtocolError('unknown_type')
  }

  const eventType = value.type as OpusChatEvent['type']
  if (
    !hasString(value.session_id)
    || !hasString(value.turn_id)
    || !hasOptionalString(value.trace_id)
  ) {
    throw new AgentStudioStreamProtocolError('invalid_shape', eventType)
  }

  const hasOptionalCorrelation = hasOptionalString(value.call_id) && hasOptionalString(value.search_id)
  let valid = hasOptionalCorrelation
  switch (eventType) {
    case 'TEXT_DELTA':
      valid = valid && typeof value.delta === 'string'
      break
    case 'TOOL_SEARCH':
      valid = valid && hasString(value.status)
      break
    case 'TOOL_SEARCH_RESULT':
      valid = valid
        && hasString(value.status)
        && typeof value.loaded_tool_count === 'number'
        && Number.isInteger(value.loaded_tool_count)
        && value.loaded_tool_count >= 0
      break
    case 'TOOL_USE':
      valid = valid && hasString(value.tool_name) && isRecord(value.tool_input)
      break
    case 'TOOL_RESULT':
      valid = valid && hasString(value.tool_name) && isRecord(value.result)
      break
    case 'PROVIDER_CONTEXT_PREFLIGHT':
      valid = valid && (value.payload_summary === undefined || isRecord(value.payload_summary))
      break
    case 'CONTEXT_OVERFLOW':
    case 'REFUSAL':
    case 'INCOMPLETE':
    case 'ERROR':
      valid = valid && hasString(value.message)
      break
    case 'DONE':
      break
  }

  if (!valid) {
    throw new AgentStudioStreamProtocolError('invalid_shape', eventType)
  }
  return value as unknown as OpusChatEvent
}

function reportAgentStudioStreamProtocolError(error: AgentStudioStreamProtocolError): void {
  logger.error(
    'Agent Studio stream protocol failure',
    new Error('Agent Studio stream protocol failure'),
    {
      component: 'agentStudioService',
      action: 'parse_chat_event',
      metadata: {
        reason: error.reason,
        eventType: error.eventType ?? 'unknown',
      },
    },
  )
}

export interface AgentStudioDurableSessionResponse {
  session_id: string
  created_at: string
  updated_at: string
  title?: string | null
  active_document_id?: string | null
}

// =============================================================================
// Agent Metadata Types
// =============================================================================

/**
 * Metadata for a single agent
 */
export interface ValidationAttachmentOption {
  attachment_id: string
  domain_pack_id: string
  domain_pack_version?: string
  validator_id: string
  validator_binding_id?: string
  validation_kind?: string
  tool_name?: string
  tool_method?: string
  validator_package_id?: string
  validator_agent_id?: string
  state: 'active' | 'under_development'
  /** Live execution availability is separate from the saved selection state. */
  available?: boolean
  unavailable_reasons?: string[]
  scope: 'pack' | 'object' | 'field'
  object_type?: string
  object_role?: string
  field_path?: string
  field_type?: string
  label: string
  target_label?: string
  description?: string
  /**
   * Curator-voice switch sentence naming what the check confirms and against
   * what. The backend always emits the key: a string on every active binding
   * (enforced by the pack contract test), null on under-development ones.
   */
  curator_label: string | null
  /** Curator-voice consequence of turning the check off. A string only when allow_opt_out is true, otherwise null. */
  when_off: string | null
  definition_state?: string
  reason?: string
  state_explanation?: string
  affected_fields?: string[]
  required: boolean
  blocking: boolean
  /** Deprecated backend metadata retained only as raw API input; UI persistence uses blocking. */
  export_blocking?: boolean
  default_enabled: boolean
  allow_opt_out: boolean
}

export interface DomainEnvelopeSchemaRef {
  schema_id: string
  provider?: string
  name?: string
  version?: string
  uri?: string
  checksum?: string
  definition_state?: string
  definition_notes?: string[]
  metadata?: Record<string, unknown>
}

export interface DomainEnvelopeModelMetadata {
  model_id: string
  display_name: string
  description?: string
  schema_ref?: DomainEnvelopeSchemaRef | null
  definition_state: string
  definition_notes: string[]
  provider_refs: Record<string, unknown>
}

export interface DomainEnvelopeFieldMetadata {
  field_path: string
  display_name?: string | null
  description?: string
  field_type: string
  required: boolean
  enum_ref?: string | null
  model_ref?: string | null
  object_type_ref?: string | null
  definition_state: string
  definition_notes: string[]
  provider_refs: Record<string, unknown>
  source_of_truth?: string | null
  validation_policy?: Record<string, unknown> | null
  validation_attachments: ValidationAttachmentOption[]
}

/**
 * Ordered field grouping declared by the domain pack's workspace_display.
 * Field paths may name fields absent from `fields`; consumers ignore unknown paths.
 */
export interface DomainEnvelopeFieldGroup {
  id: string
  label: string
  field_paths: string[]
}

export interface DomainEnvelopeObjectMetadata {
  object_type: string
  display_name: string
  description?: string
  object_role?: string | null
  model_ref?: string | null
  schema_ref?: DomainEnvelopeSchemaRef | null
  definition_state: string
  definition_notes: string[]
  provider_refs: Record<string, unknown>
  validation_attachments: ValidationAttachmentOption[]
  fields: DomainEnvelopeFieldMetadata[]
  field_groups: DomainEnvelopeFieldGroup[]
}

export interface DomainEnvelopeValidationSummary {
  total: number
  by_state: Record<'active' | 'under_development', number>
  by_scope: Record<'pack' | 'object' | 'field', number>
  default_enabled: number
  required: number
  blocking: number
  /** Deprecated backend summary retained only as raw API input; UI labels use blocking. */
  export_blocking?: number
  opt_out_allowed: number
}

export interface DomainEnvelopeMetadata {
  domain_pack_id: string
  domain_pack_version: string
  display_name: string
  description?: string
  status: string
  metadata_api_version: string
  schema_refs: DomainEnvelopeSchemaRef[]
  provider_refs: Record<string, unknown>
  semantic_source_note: string
  source_of_truth_notes: string[]
  validation_attachments: ValidationAttachmentOption[]
  model_definitions: DomainEnvelopeModelMetadata[]
  object_definitions: DomainEnvelopeObjectMetadata[]
  validation_summary: DomainEnvelopeValidationSummary
}

export interface AgentMetadata {
  execution_receipt?: import('@/types/agentExecution').AgentExecutionReceipt | null
  default_export_execution_mode?: 'ai' | 'direct'
  output_formatter_format?: 'csv' | 'tsv' | 'json' | null
  name: string
  icon: string
  category: string
  subcategory?: string
  output_schema_key?: string | null
  is_active?: boolean
  visible?: boolean
  allowed_group_ids?: string[]
  produces_flow_artifacts?: boolean
  supervisor_tool?: string
  validation_attachments?: ValidationAttachmentOption[]
  domain_envelope?: DomainEnvelopeMetadata | null
  domain_extraction_ref?: import('@/types/agentExecution').DomainExtractionRef | null
}

/**
 * Response from /registry/metadata endpoint
 */
export interface RegistryMetadataResponse {
  validator_output_schema_keys: string[]
  agents: Record<string, AgentMetadata>
}

// =============================================================================
// Agent Metadata API
// =============================================================================

/**
 * Fetch agent metadata for frontend display
 * Returns icons, names, and categories for all agents
 */
export async function fetchRegistryMetadata(): Promise<RegistryMetadataResponse> {
  const response = await fetch(`${BASE_URL}/registry/metadata`)
  if (!response.ok) {
    throw new Error(`Failed to fetch registry metadata: ${response.status}`)
  }
  return response.json()
}

// =============================================================================
// Prompt Catalog API
// =============================================================================

/**
 * Fetch the prompt catalog (all agents organized by category)
 */
export async function fetchPromptCatalog(): Promise<PromptCatalog> {
  const response = await fetch(`${BASE_URL}/catalog`)
  if (!response.ok) {
    throw new Error(`Failed to fetch catalog: ${response.status}`)
  }
  const data = await response.json()
  return data.catalog
}

/**
 * Force refresh the prompt catalog
 */
export async function refreshPromptCatalog(): Promise<PromptCatalog> {
  const response = await fetch(`${BASE_URL}/catalog/refresh`, {
    method: 'POST',
  })
  if (!response.ok) {
    throw new Error(`Failed to refresh catalog: ${response.status}`)
  }
  const data = await response.json()
  return data.catalog
}

/**
 * Get combined prompt (base + group rules injected)
 */
export async function fetchCombinedPrompt(
  agentId: string,
  groupId: string
): Promise<CombinedPromptResponse> {
  const response = await fetch(`${BASE_URL}/catalog/combined`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ agent_id: agentId, group_id: groupId }),
  })
  if (!response.ok) {
    throw new Error(`Failed to fetch combined prompt: ${response.status}`)
  }
  return response.json()
}

/**
 * Get resolved prompt preview for system/custom agents.
 */
export async function fetchPromptPreview(
  agentId: string,
  groupId?: string
): Promise<PromptPreviewResponse> {
  const params = new URLSearchParams()
  if (groupId) {
    params.set('group_id', groupId)
  }
  const query = params.toString() ? `?${params.toString()}` : ''
  const response = await fetch(`${BASE_URL}/prompt-preview/${encodeURIComponent(agentId)}${query}`)
  if (!response.ok) {
    throw new Error(`Failed to fetch prompt preview: ${response.status}`)
  }
  return response.json()
}

// =============================================================================
// Custom Agent API (Agent Workshop)
// =============================================================================

export interface CreateCustomAgentRequest {
  visibility?: 'private' | 'project'
  clone_source_agent_id?: string
  clone_source_updated_at?: string
  template_source?: string
  name: string
  custom_prompt?: string
  group_prompt_overrides?: Record<string, string>
  description?: string
  icon?: string
  include_group_rules?: boolean
  model_id?: string
  model_temperature?: number
  default_export_execution_mode?: 'ai' | 'direct'
  model_reasoning?: string | null
  tool_ids?: string[]
  output_schema_key?: string | null
  category?: string
  allowed_group_ids?: string[]
  output_contract?: AgentOutputContract
  new_generic_profile?: GenericProfileContract
}

export interface UpdateCustomAgentRequest {
  revise_generic_profile?: { base: GenericProfilePin; contract: GenericProfileContract }
  visibility?: 'private' | 'project'
  expected_updated_at?: string
  expected_revision_id?: string
  name?: string
  custom_prompt?: string
  group_prompt_overrides?: Record<string, string>
  description?: string
  icon?: string
  include_group_rules?: boolean
  model_id?: string
  model_temperature?: number
  default_export_execution_mode?: 'ai' | 'direct'
  model_reasoning?: string | null
  tool_ids?: string[]
  output_schema_key?: string | null
  allow_empty_tool_ids?: boolean
  notes?: string
  allowed_group_ids?: string[]
  output_contract?: AgentOutputContract
  new_generic_profile?: GenericProfileContract
}

export interface CloneAgentRequest {
  name?: string
  allowed_group_ids?: string[]
}

export interface ShareCustomAgentRequest {
  visibility: 'private' | 'project'
}

export interface CreateToolIdeaRequest {
  title: string
  description: string
  opus_conversation?: ToolIdeaConversationEntry[]
}

export interface CustomAgentTestRequest {
  input: string
  group_id?: string
  document_id?: string
  session_id?: string
}

export interface ActiveChatDocumentResponse {
  active: boolean
  document?: {
    id: string
    filename?: string
  }
  message?: string
}

export interface ListCustomAgentsResponse {
  custom_agents: CustomAgent[]
  total: number
}

export interface ModelsResponse {
  models: ModelOption[]
}

export interface ToolLibraryResponse {
  tools: ToolLibraryItem[]
}

export interface AgentTemplatesResponse {
  templates: AgentTemplate[]
  group_options: GroupOption[]
}

export interface ToolIdeasResponse {
  tool_ideas: ToolIdeaRequest[]
  total: number
}

export async function fetchModelOptions(): Promise<ModelOption[]> {
  const response = await fetch(`${BASE_URL}/models`)
  if (!response.ok) {
    throw new Error(`Failed to fetch model options: ${response.status}`)
  }
  const data: ModelsResponse = await response.json()
  return data.models
}

export async function fetchToolLibrary(): Promise<ToolLibraryItem[]> {
  const response = await fetch(`${BASE_URL}/tools/library`)
  if (!response.ok) {
    throw new Error(`Failed to fetch tool library: ${response.status}`)
  }
  const data: ToolLibraryResponse = await response.json()
  return data.tools
}

export async function fetchAgentTemplates(): Promise<AgentTemplatesResponse> {
  const response = await fetch(`${BASE_URL}/agents/templates`)
  if (!response.ok) {
    throw new Error(`Failed to fetch agent templates: ${response.status}`)
  }
  const data: AgentTemplatesResponse = await response.json()
  return data
}

export async function submitToolIdeaRequest(
  request: CreateToolIdeaRequest
): Promise<OwnedToolIdeaRequest> {
  const response = await fetch(`${BASE_URL}/tool-ideas`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  })
  if (!response.ok) {
    throw new Error(`Failed to submit tool idea request: ${response.status}`)
  }
  return response.json()
}

export async function listToolIdeaRequests(): Promise<ToolIdeasResponse> {
  const response = await fetch(`${BASE_URL}/tool-ideas`)
  if (!response.ok) {
    throw new Error(`Failed to list tool idea requests: ${response.status}`)
  }
  return response.json()
}

export async function listCustomAgents(templateSource?: string, scope: 'owned' | 'visible' = 'owned'): Promise<ListCustomAgentsResponse> {
  const params = new URLSearchParams()
  if (scope === 'visible') params.set('scope', scope)
  if (templateSource) {
    params.set('template_source', templateSource)
  }
  const query = params.toString() ? `?${params.toString()}` : ''
  const response = await fetch(`${BASE_URL}/custom-agents${query}`)
  if (!response.ok) {
    throw new Error(`Failed to list custom agents: ${response.status}`)
  }
  return response.json()
}

export async function createCustomAgent(
  request: CreateCustomAgentRequest
): Promise<CustomAgent> {
  const response = await fetch(`${BASE_URL}/custom-agents`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  })
  if (!response.ok) {
    throw new Error(await readCurationApiError(response))
  }
  return response.json()
}

export async function getCustomAgent(customAgentId: string): Promise<CustomAgent> {
  const response = await fetch(`${BASE_URL}/custom-agents/${encodeURIComponent(customAgentId)}`)
  if (!response.ok) {
    throw new Error(`Failed to get custom agent: ${response.status}`)
  }
  return response.json()
}

export async function updateCustomAgent(
  customAgentId: string,
  request: UpdateCustomAgentRequest
): Promise<CustomAgent> {
  const response = await fetch(`${BASE_URL}/custom-agents/${encodeURIComponent(customAgentId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  })
  if (!response.ok) {
    throw new Error(await readCurationApiError(response))
  }
  return response.json()
}

export async function getWorkshopSavedReference(customAgentId: string): Promise<{ agent_id: string; agent_revision_id: string; name: string }> {
  const response = await fetch(`${BASE_URL}/custom-agents/${encodeURIComponent(customAgentId)}/authoring-reference`)
  if (!response.ok) throw new Error('The saved agent is not available in the current flow catalog.')
  return response.json()
}

export async function validateWorkshopAction(action: WorkshopActionRequest, context: ChatContext): Promise<WorkshopAction> {
  const response = await fetch(`${BASE_URL}/custom-agents/authoring-actions/validate`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, context }),
  })
  if (!response.ok) throw new Error(await readCurationApiError(response))
  return response.json()
}

export async function getWorkshopCloneSource(customAgentId: string): Promise<CustomAgent> {
  const response = await fetch(`${BASE_URL}/custom-agents/${encodeURIComponent(customAgentId)}/clone-source`)
  if (!response.ok) throw new Error(await readCurationApiError(response))
  return response.json()
}

export async function listAgentExecutionRevisions(
  customAgentId: string, beforeRevision?: number,
): Promise<AgentExecutionRevisionPage> {
  const query = beforeRevision === undefined ? '' : `?before_revision=${beforeRevision}`
  const response = await fetch(`${BASE_URL}/custom-agents/${encodeURIComponent(customAgentId)}/execution-revisions${query}`)
  if (!response.ok) throw new Error(await readCurationApiError(response))
  return response.json()
}

export async function getAgentExecutionRevision(
  customAgentId: string, revisionId: string,
): Promise<AgentExecutionRevision> {
  const response = await fetch(`${BASE_URL}/custom-agents/${encodeURIComponent(customAgentId)}/execution-revisions/${encodeURIComponent(revisionId)}`)
  if (!response.ok) throw new Error(await readCurationApiError(response))
  return response.json()
}

export async function getAgentExecutionProfileRevision(
  customAgentId: string, revisionId: string,
): Promise<import('./genericProfileService').GenericProfileRevision> {
  const response = await fetch(`${BASE_URL}/custom-agents/${encodeURIComponent(customAgentId)}/execution-revisions/${encodeURIComponent(revisionId)}/output-profile`)
  if (!response.ok) throw new Error(await readCurationApiError(response))
  return response.json()
}

export async function restoreAgentExecutionRevision(
  customAgentId: string, revisionId: string, expectedRevisionId: string,
): Promise<CustomAgent> {
  const response = await fetch(`${BASE_URL}/custom-agents/${encodeURIComponent(customAgentId)}/execution-revisions/${encodeURIComponent(revisionId)}/restore`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ expected_revision_id: expectedRevisionId }),
  })
  if (!response.ok) throw new Error(await readCurationApiError(response))
  return response.json()
}

export async function deleteCustomAgent(customAgentId: string): Promise<void> {
  const response = await fetch(`${BASE_URL}/custom-agents/${encodeURIComponent(customAgentId)}`, {
    method: 'DELETE',
  })
  if (!response.ok) {
    throw new Error(`Failed to delete custom agent: ${response.status}`)
  }
}

export async function cloneAgentToWorkshop(
  sourceAgentId: string,
  request: CloneAgentRequest = {}
): Promise<CustomAgent> {
  const response = await fetch(
    `${BASE_URL}/agents/${encodeURIComponent(sourceAgentId)}/clone`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    }
  )
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(extractErrorMessage(error, 'Could not clone this agent. Refresh the library and try again. If it still fails, ask the owner to check its sharing and saved output structure.'))
  }
  return response.json()
}

export async function setCustomAgentVisibility(
  agentId: string,
  visibility: 'private' | 'project'
): Promise<CustomAgent> {
  const payload: ShareCustomAgentRequest = { visibility }
  const response = await fetch(
    `${BASE_URL}/agents/${encodeURIComponent(agentId)}/share`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  )
  if (!response.ok) {
    throw new Error(`Failed to update custom agent visibility: ${response.status}`)
  }
  return response.json()
}

export async function listCustomAgentVersions(customAgentId: string): Promise<CustomAgentVersion[]> {
  const response = await fetch(`${BASE_URL}/custom-agents/${encodeURIComponent(customAgentId)}/versions`)
  if (!response.ok) {
    throw new Error(`Failed to list custom agent versions: ${response.status}`)
  }
  return response.json()
}

export async function fetchActiveChatDocument(): Promise<ActiveChatDocumentResponse> {
  const response = await fetch('/api/chat/document')
  if (!response.ok) {
    throw new Error(`Failed to fetch active document: ${response.status}`)
  }
  return response.json()
}

export async function* streamCustomAgentTest(
  customAgentId: string,
  request: CustomAgentTestRequest
): AsyncGenerator<Record<string, unknown>> {
  const response = await fetch(
    `${BASE_URL}/custom-agents/${encodeURIComponent(customAgentId)}/test`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    }
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
    throw new Error(error.detail || `Failed to run custom agent test: ${response.status}`)
  }

  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('No response body from custom agent test')
  }

  yield* streamSseReader(reader)
}

export async function* streamAgentTest(
  agentId: string,
  request: CustomAgentTestRequest
): AsyncGenerator<Record<string, unknown>> {
  const response = await fetch(
    `${BASE_URL}/test-agent/${encodeURIComponent(agentId)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    }
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
    throw new Error(error.detail || `Failed to run agent test: ${response.status}`)
  }

  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('No response body from agent test')
  }

  yield* streamSseReader(reader)
}

async function* streamSseReader(
  reader: ReadableStreamDefaultReader<Uint8Array>
): AsyncGenerator<Record<string, unknown>> {

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        try {
          const event = JSON.parse(line.slice(6)) as Record<string, unknown>
          yield event
        } catch {
          // Ignore malformed partial events.
        }
      }
    }

    if (buffer.startsWith('data: ')) {
      try {
        const event = JSON.parse(buffer.slice(6)) as Record<string, unknown>
        yield event
      } catch {
        // Ignore incomplete final event.
      }
    }
  } finally {
    reader.releaseLock()
  }
}

/**
 * Fetch trace context for display
 */
export async function fetchTraceContext(
  traceId: string
): Promise<TraceContext | null> {
  const response = await fetch(`${BASE_URL}/trace/${traceId}/context`)
  if (response.status === 404) {
    return null
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch trace context: ${response.status}`)
  }
  const data = await response.json()
  return data.context
}

export async function createAgentStudioSession(): Promise<AgentStudioDurableSessionResponse> {
  const response = await fetch('/api/chat/session', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      chat_kind: AGENT_STUDIO_CHAT_HISTORY_KIND,
    }),
  })

  if (!response.ok) {
    throw new Error(await readCurationApiError(response))
  }

  const data = await response.json() as AgentStudioDurableSessionResponse
  const sessionId = normalizeChatHistoryValue(data.session_id)
  if (!sessionId) {
    throw new Error('Agent Studio session response did not include a session ID')
  }

  return {
    ...data,
    session_id: sessionId,
  }
}

export async function fetchAgentStudioHistoryList(
  request: Omit<ChatHistoryListRequest, 'chatKind'> = {},
): Promise<ChatHistoryListResponse> {
  return fetchChatHistoryList({
    ...request,
    chatKind: AGENT_STUDIO_CHAT_HISTORY_KIND,
  })
}

export async function fetchAgentStudioSessionDetail(
  request: Omit<ChatHistoryDetailRequest, 'chatKind'>,
): Promise<ChatHistoryDetailResponse> {
  return fetchChatHistoryDetail({
    ...request,
    chatKind: AGENT_STUDIO_CHAT_HISTORY_KIND,
  })
}

/**
 * Stream Agent Studio AI Chat
 * Returns an async generator that yields SSE events
 *
 * Uses effort="medium" on the backend for optimal quality/cost balance.
 *
 * @param messages - Chat messages to send
 * @param context - Optional context (selected agent, group, trace, etc.)
 * @param sessionId - Durable Agent Studio session to attach to the streamed turn
 */
export async function stopAgentStudioChat(sessionId: string, turnId: string): Promise<void> {
  const response = await fetch(`${BASE_URL}/chat/stop`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId, turn_id: turnId }),
  })
  // A completed turn can race Stop; the requested turn must never cancel its successor.
  if (!response.ok && response.status !== 409) throw new Error('Could not stop AI Chat. Please try again.')
}

export interface StudioApplicationEvent {
  kind: 'draft_applied'
  event_id: string
  output_mode_node_ids: string[]
}

export async function* streamOpusChat(
  messages: ChatMessage[],
  context?: ChatContext,
  sessionId?: string,
  applicationEvent?: StudioApplicationEvent,
): AsyncGenerator<OpusChatEvent> {
  const requestContext = sessionId
    ? {
        ...(context ?? {}),
        session_id: sessionId,
      }
    : context

  const response = await fetch(`${BASE_URL}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages, context: requestContext, application_event: applicationEvent }),
  })

  if (!response.ok) {
    throw new Error(`Chat request failed: ${response.status}`)
  }

  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('No response body')
  }

  const decoder = new TextDecoder()
  let buffer = ''
  let responseSessionId: string | null = null
  let responseTurnId: string | null = null
  let receivedTerminalEvent = false

  const parseAndCorrelate = (payload: string): OpusChatEvent => {
    let decoded: unknown
    try {
      decoded = JSON.parse(payload)
    } catch {
      const error = new AgentStudioStreamProtocolError('malformed_json')
      reportAgentStudioStreamProtocolError(error)
      throw error
    }

    let event: OpusChatEvent
    try {
      event = parseAgentStudioChatEvent(decoded)
    } catch (error) {
      if (error instanceof AgentStudioStreamProtocolError) {
        reportAgentStudioStreamProtocolError(error)
      }
      throw error
    }

    if (responseSessionId === null) {
      responseSessionId = event.session_id
      responseTurnId = event.turn_id
    } else if (event.session_id !== responseSessionId || event.turn_id !== responseTurnId) {
      const error = new AgentStudioStreamProtocolError('correlation_mismatch', event.type)
      reportAgentStudioStreamProtocolError(error)
      throw error
    }
    if (
      event.type === 'DONE'
      || event.type === 'CONTEXT_OVERFLOW'
      || event.type === 'REFUSAL'
      || event.type === 'INCOMPLETE'
      || event.type === 'ERROR'
    ) {
      receivedTerminalEvent = true
    }
    return event
  }

  const eventPayload = (line: string): string | null => {
    if (!line.startsWith('data:')) return null
    return line.slice(5).trimStart()
  }

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        const payload = eventPayload(line)
        if (payload !== null && payload.length > 0) {
          yield parseAndCorrelate(payload)
        }
      }
    }

    buffer += decoder.decode()

    // Process a complete final event even when the stream omits a newline.
    const payload = eventPayload(buffer)
    if (payload !== null && payload.length > 0) {
      yield parseAndCorrelate(payload)
    }

    if (!receivedTerminalEvent) {
      const error = new AgentStudioStreamProtocolError('missing_terminal')
      reportAgentStudioStreamProtocolError(error)
      throw error
    }
  } finally {
    reader.releaseLock()
  }
}

/**
 * Submit a manual prompt suggestion
 */
export async function submitSuggestion(
  suggestion: SuggestionSubmission
): Promise<SuggestionResponse> {
  const response = await fetch(`${BASE_URL}/suggestions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(suggestion),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
    throw new Error(error.detail || `Failed to submit suggestion: ${response.status}`)
  }

  return response.json()
}

// =============================================================================
// Tool Details API
// =============================================================================

/**
 * Fetch details for a specific tool.
 * Optionally pass agentId to get agent-specific context for multi-method tools.
 *
 * @param toolId - The tool identifier (e.g., "agr_curation_query", "search_document")
 * @param agentId - Optional agent ID to get agent-specific method context
 */
export async function fetchToolDetails(
  toolId: string,
  agentId?: string
): Promise<ToolInfo> {
  const params = agentId ? `?agent_id=${encodeURIComponent(agentId)}` : ''
  const response = await fetch(`${BASE_URL}/tools/${encodeURIComponent(toolId)}${params}`)

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Tool "${toolId}" not found`)
    }
    throw new Error(`Failed to fetch tool details: ${response.status}`)
  }

  const data = await response.json()
  return data.tool
}

/**
 * Fetch all available tools.
 * Returns a record of tool_id -> ToolInfo
 */
export async function fetchAllTools(): Promise<Record<string, ToolInfo>> {
  const response = await fetch(`${BASE_URL}/tools`)

  if (!response.ok) {
    throw new Error(`Failed to fetch tools: ${response.status}`)
  }

  const data = await response.json()
  return data.tools
}

// =============================================================================
// Flow Management API
// =============================================================================

import type {
  FlowResponse,
  FlowListResponse,
  CreateFlowRequest,
  UpdateFlowRequest,
  FlowVisibility,
  FlowDefinition,
} from '@/components/AgentStudio/FlowBuilder/types'

export type {
  FlowListResponse,
  FlowSummaryResponse,
  FlowResponse,
  FlowVisibility,
  FlowAccess,
} from '@/components/AgentStudio/FlowBuilder/types'

const FLOWS_URL = '/api/flows'
export const DEFAULT_FLOW_LIST_PAGE_SIZE = 50

class FlowListLoadError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'FlowListLoadError'
  }
}

function createFlowListLoadError(status?: number): FlowListLoadError {
  if (status === 401) {
    return new FlowListLoadError('Please log in to view your flows')
  }

  if (typeof status === 'number') {
    return new FlowListLoadError(`Failed to load flows (${status})`)
  }

  return new FlowListLoadError('Failed to connect to server')
}

/**
 * List owned and authorized project-shared flows
 */
export async function listFlows(page = 1, pageSize = DEFAULT_FLOW_LIST_PAGE_SIZE): Promise<FlowListResponse> {
  let response: Response
  try {
    response = await fetch(`${FLOWS_URL}?page=${page}&page_size=${pageSize}`, {
      credentials: 'include',
    })
  } catch {
    throw createFlowListLoadError()
  }

  if (!response.ok) {
    throw createFlowListLoadError(response.status)
  }

  try {
    return await response.json()
  } catch (error) {
    if (error instanceof Error) {
      throw error
    }

    throw new FlowListLoadError(`Unexpected error while loading flows: ${String(error)}`)
  }
}

/** Collect the visible browse list across the server's paginated responses. */
export async function listAllFlows(): Promise<FlowListResponse> {
  const first = await listFlows()
  const flows = [...first.flows]
  for (let page = 2; page <= Math.ceil(first.total / first.page_size); page += 1) {
    const next = await listFlows(page, first.page_size)
    flows.push(...next.flows)
  }
  return { ...first, flows }
}

/**
 * Get a single flow by ID
 */
export async function getFlow(flowId: string): Promise<FlowResponse> {
  const response = await fetch(`${FLOWS_URL}/${flowId}`)
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(extractErrorMessage(error, `Failed to get flow: ${response.status}`))
  }
  return response.json()
}

/**
 * Helper to extract error message from API response
 */
function validationFieldPath(loc: unknown): string {
  if (!Array.isArray(loc)) return ''
  const parts = loc[0] === 'body' ? loc.slice(1) : loc
  return parts.reduce<string>((path, part) => {
    if (typeof part === 'number') return `${path}[${part}]`
    if (typeof part !== 'string' || !part) return path
    return path ? `${path}.${part}` : part
  }, '')
}

function extractErrorMessage(error: { detail?: unknown }, fallback: string): string {
  if (typeof error.detail === 'string') return error.detail
  if (Array.isArray(error.detail)) {
    return error.detail.map((entry) => {
      if (!isRecord(entry)) return fallback
      const message = entry.msg || entry.message || fallback
      const path = validationFieldPath(entry.loc)
      return path ? `${path}: ${message}` : message
    }).join('; ')
  }
  if (isRecord(error.detail) && Array.isArray(error.detail.findings)) {
    const messages = error.detail.findings
      .filter((finding) => isRecord(finding) && finding.severity === 'error' && typeof finding.message === 'string')
      .map((finding) => [finding.message, finding.fix_hint].filter((value) => typeof value === 'string' && value.trim()).join(' '))
    if (messages.length) return messages[0]
  }
  return fallback
}

/** Preview the selected revision and its canonical automatic checks; no writes. */
export async function selectFlowRevision(
  flowDefinition: FlowDefinition, nodeId: string, agentRevisionId: string,
): Promise<import('@/components/AgentStudio/FlowBuilder/types').AgentNodeData> {
  const response = await fetch(`${FLOWS_URL}/select-revision`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ flow_definition: flowDefinition, node_id: nodeId, agent_revision_id: agentRevisionId }),
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(extractErrorMessage(error, 'Could not refresh this revision’s automatic checks. Try again.'))
  }
  return response.json()
}

/**
 * Create a new flow
 */
export async function createFlow(flow: CreateFlowRequest): Promise<FlowResponse> {
  const response = await fetch(FLOWS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(flow),
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
    throw new Error(extractErrorMessage(error, `Failed to create flow: ${response.status}`))
  }
  return response.json()
}

/**
 * Update an existing flow
 */
export async function updateFlow(
  flowId: string,
  updates: UpdateFlowRequest
): Promise<FlowResponse> {
  const response = await fetch(`${FLOWS_URL}/${flowId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }))
    throw new Error(extractErrorMessage(error, `Failed to update flow: ${response.status}`))
  }
  return response.json()
}

export interface FlowDraftValidationResponse {
  restoration_only?: boolean
  projection_fields_by_node?: Record<string, {
    execution_receipt: AgentExecutionReceipt | null
    schema_fingerprint?: string
    fields: Array<{
      ref: string
      profile_path?: string
      group?: string
      label: string
      value_type: string
      schema_kind: string
      array_depth: number
      required: boolean
      nullable: boolean
      enum_values: string[]
    }>
  }>
  artifact_kind: 'flow'
  phase: 'pre_apply' | 'post_apply'
  valid: boolean
  findings: Array<{
    code: string
    severity: 'error' | 'warning' | 'info'
    path: string
    message: string
    fix_hint?: string
  }>
}

/** Canonically validate a transient proposal candidate without writing it. */
export async function validateWorkshopDraft(
  workshop: import('@/types/promptExplorer').AgentWorkshopContext,
  phase: 'pre_apply' | 'post_apply',
): Promise<FlowDraftValidationResponse> {
  const response = await fetch('/api/agent-studio/custom-agents/validate-draft', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ workshop, phase }),
  })
  if (!response.ok) throw new Error(`Workshop validation failed (${response.status}).`)
  return response.json()
}

export async function validateFlowDraft(
  flowDefinition: FlowDefinition,
  phase: 'pre_apply' | 'post_apply',
  expectedDraftFingerprint: string,
  currentDraftFingerprint: string,
  restorationBase?: FlowDefinition,
): Promise<FlowDraftValidationResponse> {
  const response = await fetch(`${FLOWS_URL}/validate-draft`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      flow_definition: flowDefinition,
      phase,
      expected_draft_fingerprint: expectedDraftFingerprint,
      current_draft_fingerprint: currentDraftFingerprint,
      ...(restorationBase ? { restoration_base: restorationBase } : {}),
    }),
  })
  if (!response.ok) {
    throw new Error(`Failed to validate flow proposal: ${response.status}`)
  }
  return response.json()
}

/**
 * Delete a flow
 */
export async function deleteFlow(flowId: string): Promise<void> {
  const response = await fetch(`${FLOWS_URL}/${flowId}`, {
    method: 'DELETE',
  })
  if (!response.ok) {
    throw new Error(`Failed to delete flow: ${response.status}`)
  }
}

/** Set visibility of an owned flow. */
export async function shareFlow(flowId: string, visibility: FlowVisibility): Promise<FlowResponse> {
  const response = await fetch(`${FLOWS_URL}/${flowId}/share`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ visibility }),
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(extractErrorMessage(error, `Failed to change flow visibility: ${response.status}`))
  }
  return response.json()
}

/** Clone a visible flow into a caller-owned private copy. */
export async function cloneFlow(flowId: string, name?: string): Promise<FlowResponse> {
  const response = await fetch(`${FLOWS_URL}/${flowId}/clone`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(name === undefined ? {} : { name }),
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(extractErrorMessage(error, `Failed to clone flow: ${response.status}`))
  }
  return response.json()
}
